import './InsertFieldPanel.scss';
import React, {useState, useMemo, useEffect} from 'react';
import {
  Box,
  Typography,
  Drawer,
  useTheme,
  useMediaQuery,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../../shared/types';
import {FieldType} from '../../CreateNewProductionUtils';
import {panelWidths} from '../../../../../shared/constants';
import {ReactComponent as RadioActiveIcon} from '../../../../../assets/images/radio-active-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../../../assets/images/radio-enable-icon.svg';

interface InsertFieldPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (field: string | number) => void;
  insertFieldTypes: ISelectItem[];
  metadataFieldOptions: ISelectItem[];
  productionFieldOptions: ISelectItem[];
}

const InsertFieldPanel: React.FC<InsertFieldPanelProps> = ({
  isOpen,
  onClose,
  onInsert,
  insertFieldTypes,
  metadataFieldOptions,
  productionFieldOptions,
}) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const width = panelWidths.small;
  const transitionDuration = 300;

  const [fieldType, setFieldType] = useState<string>(FieldType.METADATA);
  const [selectedField, setSelectedField] = useState<string | number>(
    metadataFieldOptions[0]?.value || ''
  );

  const currentFieldOptions = useMemo(() => {
    return fieldType === FieldType.METADATA
      ? metadataFieldOptions
      : productionFieldOptions;
  }, [fieldType, metadataFieldOptions, productionFieldOptions]);

  // Sync selectedField when options load asynchronously or when panel opens
  useEffect(() => {
    if (isOpen && currentFieldOptions.length > 0) {
      // Use functional update to avoid stale closure issues
      setSelectedField((prevSelected) => {
        // If no field is selected or the selected field is not in current options, select the first one
        const isSelectedFieldValid = currentFieldOptions.some(
          (option) => option.value === prevSelected
        );
        if (!isSelectedFieldValid || !prevSelected) {
          return currentFieldOptions[0]?.value || '';
        }
        return prevSelected;
      });
    }
  }, [isOpen, currentFieldOptions]);

  const handleFieldTypeChange = (value: string) => {
    setFieldType(value);
    const newOptions =
      value === FieldType.METADATA
        ? metadataFieldOptions
        : productionFieldOptions;
    setSelectedField(newOptions[0]?.value || '');
  };

  const handleInsertConfirm = () => {
    if (selectedField) {
      onInsert(selectedField);
    }
  };

  return (
    <Drawer
      anchor={'right'}
      transitionDuration={transitionDuration}
      open={isOpen}
      className="arr-panel"
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      ModalProps={{
        keepMounted: true,
        BackdropProps: {
          className: 'arr-drawer__backdrop',
        },
      }}
      slotProps={{
        paper: {
          className: `arr-drawer__panel`,
          sx: {
            width: isSmallScreen ? '100%' : `${width}rem`,
          },
        },
      }}
    >
      <Box className="arr-drawer__content">
        <Box className="insert-field-container">
          <Box className="insert-field-header-box">
            <Box className="title-box">
              <Typography
                className=" typo-text headline3_size title"
                data-testid={`insert-field-panel-insert-field-title`}>
                {t(
                  'productionsAndExports.createNewProduction.common.toolbar.insertFieldTitle'
                )}
              </Typography>
            </Box>
            <Box className="action-buttons-box">
              <ArrButton
                variant="secondary"
                size="medium"
                onClick={onClose}
                disabled={false}
                dataTestId="insert-field-panel-cancel-button"
              >
                {t('common.cancel')}
              </ArrButton>
              <ArrButton
                variant="primary"
                size="medium"
                type="submit"
                dataTestId="insert-field-button"
                disabled={false}
                onClick={handleInsertConfirm}
              >
                {t('common.select')}
              </ArrButton>
            </Box>
          </Box>
          <Box className="insert-field-panel">
            <Box className="insert-field-content">
              {/* Insert Field Method Radio Buttons */}
              <Box className="insert-field-type-section">
                <RadioGroup
                  row
                  value={fieldType}
                  data-testid={"insert-field-type-section-radio-group"}
                  onChange={(e) => {
                    const selectedObj = insertFieldTypes?.find(
                      (opt) => opt.value === e.target.value
                    );
                    if (selectedObj)
                      handleFieldTypeChange(
                        String(selectedObj.value) || FieldType.METADATA
                      );
                  }}
                  className="custom-radio-group typo-text body3_text"
                  aria-label={t(
                    'productionsAndExports.createNewProduction.common.toolbar.insertFieldTitle'
                  )}
                >
                  {insertFieldTypes?.map(({value, label}) => {
                    return (
                      <FormControlLabel
                      data-testid="insert-field-types-label"
                        key={value}
                        value={value}
                        control={
                          <Radio
                            icon={<RadioEnableIcon data-testid="insert-field-types-radio-enable-icon"/>}
                            checkedIcon={<RadioActiveIcon data-testid="insert-field-types-radio-active-icon"/>}
                            slotProps={{
                              input: {
                                'aria-label': String(label),
                                'data-testid': `radio-${value}`
                              } as React.InputHTMLAttributes<HTMLInputElement>,
                            }}
                            
                          />
                        }
                        label={label}
                      />
                    );
                  })}
                </RadioGroup>
              </Box>

              <Box className="insert-field-section">
                <ArrSingleSelectDropdown
                  dropdownLabel={
                    String(currentFieldOptions[0]?.label) ||
                    t(
                      'productionsAndExports.createNewProduction.common.toolbar.selectFieldLabel'
                    )
                  }
                  dropdownItems={currentFieldOptions || []}
                  dropdownItemKey="value"
                  dropdownItemDisplay="label"
                  selectedValues={
                    currentFieldOptions?.find(
                      (item) => item.value === selectedField
                    ) || null
                  }
                  onSelectionChange={(item) => {
                    setSelectedField(String(item?.value));
                  }}
                  sizeVariant="medium"
                  searchable
                  data-testid="asd-insert-field-single-select"
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default InsertFieldPanel;
